import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, GraduationCap, BarChart3, Search, MessageCircle, ArrowUpRight } from 'lucide-react';
import { SERVICES } from '@/lib/services';
import { trackBookCall } from '@/lib/gtm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png";

const intelligenceSuite = [
  { name: "Wizbang EduOS", desc: "School Management OS", icon: GraduationCap, color: "text-blue-600", bgColor: "bg-blue-100", comingSoon: true },
  { name: "Wizbang AdOS", desc: "Ads Analytics AI", icon: BarChart3, color: "text-[#ff5a1f]", bgColor: "bg-[#ff5a1f]/15", comingSoon: true },
  { name: "Wizbang SearchOS", desc: "Website Auditor & SEO AI", icon: Search, color: "text-emerald-600", bgColor: "bg-emerald-100", comingSoon: true },
  { name: "WizbangChatOS", desc: "WhatsApp API Platform", icon: MessageCircle, color: "text-purple-600", bgColor: "bg-purple-100", comingSoon: true },
];

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Work", path: "/work" },
  { name: "Blog", path: "/blog" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [suiteOpen, setSuiteOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b" data-testid="navbar" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Wizbang" className="h-9 w-auto" style={{ filter: 'invert(1) brightness(0.1)' }} />
            <span className="hidden md:inline font-syne text-lg font-bold text-[#0A0A0A] tracking-tight"></span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 2).map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  location.pathname === link.path ? "text-[#ff5a1f]" : "text-[#5B5B5B] hover:text-[#0A0A0A]"
                }`}>
                {link.name}
              </Link>
            ))}

            {/* Services Mega Menu */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <Link to="/services" data-testid="nav-services"
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  location.pathname.startsWith('/services') ? 'text-[#ff5a1f]' : 'text-[#5B5B5B] hover:text-[#0A0A0A]'
                }`}>
                Services
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
              </Link>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4" data-testid="services-mega-menu">
                  <div className="bg-white border rounded-lg p-3 w-[640px] grid grid-cols-2 gap-1 shadow-2xl" style={{ borderColor: 'var(--border)' }}>
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`menu-service-${s.slug}`}
                        className="flex items-start gap-3 p-3 rounded-md hover:bg-[#F4F2EC] transition-colors group">
                        <div className="p-2 rounded-md bg-[#ff5a1f]/10 flex-shrink-0">
                          <s.icon className="w-5 h-5 text-[#ff5a1f]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#0A0A0A] text-sm group-hover:text-[#ff5a1f] transition-colors flex items-center gap-1">
                            {s.title}
                            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </h4>
                          <p className="text-xs text-[#5B5B5B] mt-0.5 line-clamp-2">{s.short}</p>
                        </div>
                      </Link>
                    ))}
                    <Link to="/services" data-testid="menu-all-services" className="col-span-2 mt-1 flex items-center justify-between p-3 rounded-md bg-[#FAFAF7] hover:bg-[#F4F2EC] transition-colors group">
                      <span className="text-sm font-semibold text-[#0A0A0A]">View all services & packages</span>
                      <ArrowUpRight className="w-4 h-4 text-[#ff5a1f] group-hover:rotate-45 transition-transform" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(2).map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  location.pathname === link.path ? "text-[#ff5a1f]" : "text-[#5B5B5B] hover:text-[#0A0A0A]"
                }`}>
                {link.name}
              </Link>
            ))}

            {/* Intelligence Suite Dropdown */}
            <div className="relative" onMouseEnter={() => setSuiteOpen(true)} onMouseLeave={() => setSuiteOpen(false)}>
              <button data-testid="intelligence-suite-btn"
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-[#5B5B5B] hover:text-[#0A0A0A] transition-colors duration-300">
                Intelligence Suite
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${suiteOpen ? 'rotate-180' : ''}`} />
              </button>
              {suiteOpen && (
                <div className="absolute top-full right-0 pt-4" data-testid="intelligence-suite-menu">
                  <div className="bg-white border rounded-lg p-4 w-[480px] grid grid-cols-2 gap-2 shadow-2xl" style={{ borderColor: 'var(--border)' }}>
                    {intelligenceSuite.map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-3 rounded-md hover:bg-[#F4F2EC] transition-colors cursor-pointer group"
                        data-testid={`suite-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div className={`p-2 rounded-md ${item.bgColor}`}>
                          <item.icon className={`w-5 h-5 ${item.color}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-[#0A0A0A] text-sm group-hover:text-[#ff5a1f] transition-colors">{item.name}</h4>
                            {item.comingSoon && (
                              <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#ff5a1f]/15 text-[#ff5a1f] rounded">Soon</span>
                            )}
                          </div>
                          <p className="text-xs text-[#5B5B5B] mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/book-appointment" data-testid="book-appointment-btn" onClick={() => trackBookCall('navbar')}
              className="bg-[#0A0A0A] text-white px-5 py-3 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors duration-300 btn-press inline-flex items-center gap-2">
              Book a call
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-[#0A0A0A]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t" style={{ borderColor: 'var(--border)' }} data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 text-base font-medium transition-colors rounded-md ${
                  location.pathname === link.path ? "text-[#ff5a1f] bg-[#ff5a1f]/10" : "text-[#0A0A0A] hover:bg-[#F4F2EC]"
                }`}>
                {link.name}
              </Link>
            ))}

            <div className="pt-2">
              <p className="px-4 py-2 text-xs text-[#8A8A8A] uppercase tracking-wider font-semibold">Services</p>
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#F4F2EC] rounded-md">
                  <s.icon className="w-5 h-5 text-[#ff5a1f]" />
                  <span className="text-[#0A0A0A] text-sm font-medium">{s.title}</span>
                </Link>
              ))}
            </div>

            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <p className="px-4 py-2 text-xs text-[#8A8A8A] uppercase tracking-wider font-semibold">Intelligence Suite</p>
              {intelligenceSuite.map((item) => (
                <div key={item.name} className="flex items-center gap-3 px-4 py-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[#0A0A0A] text-sm font-medium">{item.name}</span>
                      {item.comingSoon && (
                        <span className="px-1.5 py-0.5 text-[8px] font-bold uppercase bg-[#ff5a1f]/15 text-[#ff5a1f] rounded">Soon</span>
                      )}
                    </div>
                    <span className="text-[#8A8A8A] text-xs">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/book-appointment" onClick={() => setMobileOpen(false)}
              className="block mt-4 bg-[#0A0A0A] text-white px-6 py-4 text-center text-sm font-semibold rounded-full">
              Book a call
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
